import React from 'react';
import { publish, events } from '@mergermarket/acuris-analytics';
import Moment from 'moment';
import styled, { css } from 'styled-components';
import { warmGrey, fontWeight, colors, breakpoints, fontFamily, divisionColors } from '@mergermarket/acuris-react-styles';
import { EventContext } from '@mergermarket/acuris-react-event-context';
import { FollowBellIcon } from '@mergermarket/acuris-react-icon';
import SituationLabel from '@mergermarket/acuris-react-situation-label';
import AcurisProprietaryIcon from '@mergermarket/acuris-react-proprietary-icon';
import moment from 'moment-timezone';
import { Checkbox, styleCheckbox } from '@mergermarket/acuris-react-checkbox';

const CommpanyContentLinksList = styled.ul`
  list-style: none;
  padding-left: 0;

  li {
    display: inline;

    span,
    a,
    a:visited {
      color: ${warmGrey.VIII};
    }

    &:not(:last-of-type):after {
      content: '\\002C\\00A0';
    }
  }
`;
const StyledCompanyContentLinkCategory = styled.span`
  font-weight: ${fontWeight.bold};
`;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const extendedISOFormat = 'YYYY-MM-DD';

const toMoment = ISOString => ISOString ? Moment(ISOString, extendedISOFormat) : undefined;

const toHumanDate = momentDate => momentDate.format('DD MMM YYYY');

const formatDate = isoDate => toHumanDate(toMoment(isoDate));

const contentLinkText = ({
  date,
  count
}) => {
  if (count) {
    return `${count.entitledTo ? `${count.entitledTo}/` : ''}${count.total}`;
  }

  if (date) return `As of ${formatDate(date)}`;
  return;
};

const CompanyContentTitle = ({
  title
}) => /*#__PURE__*/React.createElement(StyledCompanyContentLinkCategory, null, `${title}: `);

const CompanyContentLinksWrapper = ({
  children
}) => /*#__PURE__*/React.createElement(CommpanyContentLinksList, null, children);
const CompanyContentLink = ({
  inactive,
  title,
  url,
  count,
  date,
  clickable = true,
  onClick
}) => /*#__PURE__*/React.createElement("li", null, clickable && url && !inactive ? /*#__PURE__*/React.createElement("a", {
  onClick: onClick,
  href: url
}, /*#__PURE__*/React.createElement(CompanyContentTitle, {
  title: title
}), `${contentLinkText({
  date,
  count
})}`) : /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(CompanyContentTitle, {
  title: title
}), `${contentLinkText({
  date,
  count
})}`));

const CompanyContentLinks = ({
  companyContentLinks,
  companyID,
  inactive
}) => {
  if (!Array.isArray(companyContentLinks) || !companyContentLinks.length) {
    return null;
  }

  return /*#__PURE__*/React.createElement(CompanyContentLinksWrapper, null, companyContentLinks.map(contentLink => /*#__PURE__*/React.createElement(CompanyContentLink, _extends({
    inactive: inactive,
    key: contentLink.title,
    onClick: event => {
      event.stopPropagation();
      publish(events.companyCardContentLinkClicked, {
        companyID,
        title: contentLink.title
      });
    }
  }, contentLink))));
};

const transitionFirstPadding = '30ms';
const transitionLastPadding = transitionFirstPadding;
const transitionWidth = '240ms';
const delayWidth = transitionFirstPadding;
const finalAnimationDelay = '270ms';
const customLabelSharedStyles = `
  display: inline-block;
  height: 18px;
  font-weight: ${fontWeight.bold};
  padding: 0;
  border: none;
  position: relative;
  margin-top: 0.4rem;
  margin-right: 0.8rem;
  font-size: 1.2rem;
  line-height: 1.8;
  outline: 0;
  cursor: pointer;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;

  &:focus{
    outline: solid 1px ${colors.outline};
  }
`;
const StyledCustomLabelInactive = styled.span`
  ${customLabelSharedStyles}
`;
const StyledCustomLabel = styled.button`
  ${customLabelSharedStyles}
`;
const StyledCustomLabelLink = styled.a`
  ${customLabelSharedStyles}
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;
const StyledCustomLabelText = styled.div`
  background-color: ${props => props.isInactive ? 'transparent' : colors.primaryBackground};
  padding: 0 0.5rem;
  text-align: left;
  font-weight: ${fontWeight.bold};
  font-size: 1rem;
  color: ${colors.primaryFont};
  border-left: 0.4rem solid;
  border-color: ${props => props.isInactive ? colors.primaryFont : warmGrey.VIII};
`;
const StyledCustomLabelTextInverse = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 0;
  overflow: hidden;
  color: ${colors.primaryFontInverse};
  padding: 0;
  border-left: 0 solid transparent;
  font-size: 1rem;
  text-align: left;
  background-color: ${warmGrey.VIII};

  transition: padding-right ${transitionFirstPadding} linear 0ms, width ${transitionWidth} linear ${delayWidth},
    padding-left ${transitionLastPadding} linear ${finalAnimationDelay},
    border-left ${transitionLastPadding} linear ${finalAnimationDelay};

  ${StyledCustomLabelLink}:hover &,
  ${StyledCustomLabel}:hover & {
    width: 100%;
    padding: 0 0.5rem;
    border-left: 0.4rem solid ${colors.primaryFont};
    transition: padding-left ${transitionFirstPadding} linear 0ms, border-left ${transitionFirstPadding} linear 0ms,
      width ${transitionWidth} linear ${delayWidth},
      padding-right ${transitionLastPadding} linear ${finalAnimationDelay};
  }
`;

const CustomLabelText = ({
  text,
  isInactive
}) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledCustomLabelText, {
  isInactive: isInactive
}, text), !isInactive && /*#__PURE__*/React.createElement(StyledCustomLabelTextInverse, {
  "aria-hidden": true
}, text));

const CustomLabel = ({
  text,
  isInactive,
  url,
  className,
  onClick
}) => {
  if (isInactive) {
    return /*#__PURE__*/React.createElement(StyledCustomLabelInactive, {
      className: className
    }, /*#__PURE__*/React.createElement(CustomLabelText, {
      text: text,
      isInactive: true
    }));
  }

  if (url) {
    return /*#__PURE__*/React.createElement(StyledCustomLabelLink, {
      className: className,
      href: url,
      onClick: onClick
    }, /*#__PURE__*/React.createElement(CustomLabelText, {
      text: text
    }));
  }

  return /*#__PURE__*/React.createElement(StyledCustomLabel, {
    className: className,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(CustomLabelText, {
    text: text
  }));
};

const StyledFollowButton = styled.button`
  width: 3.2rem;
  height: 3.2rem;
  border: none;
  cursor: pointer;
  display: inline;
  padding: 0;
  background: transparent;

  @media (min-width: ${breakpoints.xsmall}) {
    position: absolute;
    top: 0.4rem;
    right: 0.4rem;
    margin: 0;
  }
`;
const StyledFollowBellIcon = styled(FollowBellIcon)`
  width: 1.6rem;
  height: 1.6rem;
`;

let EFollowButtonEvent;

(function (EFollowButtonEvent) {
  EFollowButtonEvent["FOLLOW"] = "follow";
  EFollowButtonEvent["UNFOLLOW"] = "unfollow";
})(EFollowButtonEvent || (EFollowButtonEvent = {}));

const FollowButton = ({
  analyticsTag,
  companyName,
  isFollowing,
  onFollowChange
}) => {
  const {
    publish
  } = React.useContext(EventContext);

  const onClick = e => {
    e.stopPropagation(); // Stop event bubbling and causing navigation

    onFollowChange && onFollowChange(!isFollowing);
    publish({
      eventType: isFollowing ? EFollowButtonEvent.UNFOLLOW : EFollowButtonEvent.FOLLOW,
      component: 'FollowButton',
      tag: analyticsTag,
      payload: {
        companyName
      }
    });
  };

  return /*#__PURE__*/React.createElement(StyledFollowButton, {
    "data-test-follow-button": true,
    onClick: onClick,
    title: isFollowing ? `Unfollow ${companyName}` : `Follow ${companyName}`
  }, /*#__PURE__*/React.createElement(StyledFollowBellIcon, {
    following: isFollowing
  }));
};

const StyledTagContainer = styled.div`
  padding-top: 0.4rem;
  padding-right: 0.4rem;

  ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  ${props => props.threeColumnAtBreakpoint && ['xlarge', 'xxlarge'].includes(props.threeColumnAtBreakpoint) && css`
      @media (min-width: ${breakpoints[props.threeColumnAtBreakpoint]}) {
        padding-left: 2.4rem;
        max-width: 29.333%;
        padding-top: 0;
        flex-basis: 29.333%;
      }
    `}

  ${props => props.cardType === 'notification' && css`
      @media (min-width: ${breakpoints.xxlarge}) {
        max-width: none;
        padding-top: 0.4rem;
        flex-basis: unset;
      }
    `};

  ${props => props.cardType === 'company' && css`
      @media (min-width: ${breakpoints.xxlarge}) {
        padding-top: 0.4rem;
      }
    `};

  ${props => props.cardType === 'company' && props.twoColumnAtBreakpoint === 'large' && css`
      @media (min-width: ${breakpoints.xlarge}) {
        padding-left: 0;
      }
    `};

  ${props => props.cardType === 'company' && props.threeColumnAtBreakpoint === 'xxlarge' && css`
      @media (min-width: ${breakpoints.xxlarge}) {
        padding-left: 2.4rem;
        flex-basis: 29.333%;
        max-width: 29.333%;
        padding-top: 0;
        padding-right: 3.2rem;
      }
    `};
`;
const StyledTagItem = styled.li`
  display: inline-block;
  color: ${warmGrey.VIII};

  font-weight: ${props => props.isPrimary ? fontWeight.bold : fontWeight.normal};

  &:not(:last-of-type):after {
    content: '\\002C\\00A0';
  }

  a {
    color: ${warmGrey.VIII};
  }
`;
const TagPill = styled.span`
  color: ${warmGrey.XI};
  font-size: 1rem;
  height: 1.8rem;
  font-weight: ${fontWeight.bold};
  background: ${warmGrey.III};
  padding: 0 0.4rem;
  margin-right: 0.4rem;
  border-radius: 0.2rem;
  float: left;
`;
const TagSource = styled.span`
  float: left;
  margin-right: 0.3rem;
  font-weight: ${fontWeight.bold};
  color: ${warmGrey.VIII};
  display: inline-block;
`;

const Tag = ({
  text,
  link,
  primary,
  onClick
}) => {
  return text ? /*#__PURE__*/React.createElement(StyledTagItem, {
    isPrimary: primary,
    "data-test-tag": primary ? 'primary' : 'secondary'
  }, link ? /*#__PURE__*/React.createElement("a", {
    href: link,
    onClick: onClick
  }, text) : text) : null;
};

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

const showTagContainer = ({
  pillTag,
  primaryTag,
  secondaryTags,
  sources
}) => {
  return pillTag || primaryTag || Array.isArray(secondaryTags) && secondaryTags.length || Array.isArray(sources) && sources.length;
};

const Tags = ({
  pillTag,
  primaryTag,
  secondaryTags,
  sources,
  twoColumnAtBreakpoint,
  threeColumnAtBreakpoint,
  cardType
}) => {
  if (!showTagContainer({
    pillTag,
    primaryTag,
    secondaryTags,
    sources
  })) {
    return null;
  }

  return /*#__PURE__*/React.createElement(StyledTagContainer, {
    cardType: cardType,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint
  }, pillTag && /*#__PURE__*/React.createElement(TagPill, {
    "data-test-tag": "pill"
  }, pillTag.text), Array.isArray(sources) && sources.length && /*#__PURE__*/React.createElement(TagSource, null, "Source: "), /*#__PURE__*/React.createElement("ul", null, Array.isArray(sources) && sources.map(source => /*#__PURE__*/React.createElement(Tag, _extends$1({}, source, {
    key: source.text,
    primary: true
  }))), Array.isArray(primaryTag) ? primaryTag.map(primaryTagItem => /*#__PURE__*/React.createElement(Tag, _extends$1({}, primaryTagItem, {
    key: primaryTagItem.text,
    primary: true
  }))) : primaryTag && /*#__PURE__*/React.createElement(Tag, _extends$1({}, primaryTag, {
    key: primaryTag.text,
    primary: true
  })), Array.isArray(secondaryTags) && secondaryTags.map(secondaryTag => /*#__PURE__*/React.createElement(Tag, _extends$1({}, secondaryTag, {
    key: secondaryTag.text
  })))));
};

const StyledCardContent = styled.div`
  @media (min-width: ${breakpoints.xsmall}) {
    max-width: 100%;
    flex-basis: 100%;
  }
`;

const StyledCardContentWrapper = styled.div`
  width: 100%;
  padding-bottom: 0.4rem;

  ${props => props.cardType === 'notification' && css`
      @media (min-width: ${breakpoints.large}) {
        max-width: none;
        padding: 0 0 0.4rem 0;
        flex-basis: unset;
      }
    `}

    ${props => {
  switch (props.twoColumnAtBreakpoint) {
    case 'medium':
      return css`
            @media (min-width: ${breakpoints.medium}) {
              max-width: calc(75% + 2.8rem);
              padding: 0.4rem 0;
              padding-left: 1.6rem;
              flex-basis: calc(75% + 2.8rem);
            }
            @media (min-width: ${breakpoints.xlarge}) {
              max-width: calc(80% + 3.6rem);
              padding-left: 2.4rem;
              flex-basis: calc(80% + 3.6rem);
            }
          `;

    case 'large':
      return css`
            @media (min-width: ${breakpoints.large}) {
              max-width: calc(75% + 2.8rem);
              ${props.hasSituationLabel && props.cardType === 'company' ? `padding: 0 0 0.4rem 0` : 'padding: 0.4rem 0'}
              padding-left: 1.6rem;
              flex-basis: calc(75% + 2.8rem);
            }
            @media (min-width: ${breakpoints.xlarge}) {
              max-width: calc(80% + 2.8rem);
              flex-basis: calc(80% + 2.8rem);
            }
          `;

    default:
      return ``;
  }
}}
      
    ${props => ['xlarge', 'xxlarge'].includes(props.threeColumnAtBreakpoint) && css`
        @media (min-width: ${breakpoints[props.threeColumnAtBreakpoint]}) {
          display: flex;
          flex-direction: row;
          ${props.cardType !== 'company' && `
            max-width: calc(80% + 3.6rem);
            flex-basis: calc(80% + 3.6rem);
            padding-left: 2.4rem;
          `}
        }
      `}
      
    h3 {
      margin: 0;
      width: 100%;
    }
`;

const StyledCardMeta = styled.div`
  position: relative;

  display: flex;

  padding: 0.4rem;
  padding-top: 0;
  padding-left: 0;

  @media (min-width: ${breakpoints.large}) {
    padding-right: 2.1rem;

    flex-basis: calc(25% - 2.4rem);
  }

  @media (min-width: ${breakpoints.xlarge}) {
    flex-basis: calc(20% - 2.4rem);
  }

  ${props => props.cardType === 'notification' && css`
      @media (min-width: ${breakpoints.large}) {
        max-width: none;
        padding-right: 0;
        flex-basis: unset;
      }
    `};

  ${props => props.twoColumnAtBreakpoint === 'medium' && css`
      @media (min-width: ${breakpoints.medium}) {
        padding-right: 2.1rem;
        flex-basis: calc(25% - 2.4rem);
      }

      @media (min-width: ${breakpoints.large}) {
        padding-right: 2.1rem;
        flex-basis: calc(25% - 2.4rem);
      }

      @media (min-width: ${breakpoints.xlarge}) {
        flex-basis: calc(20% - 2.4rem);
      }

      @media (min-width: ${breakpoints.xxlarge}) {
        flex-basis: calc(20% - 1.4rem);
      }
    `};

  ${props => props.cardType === 'company' && props.twoColumnAtBreakpoint === 'medium' && css`
      @media (min-width: ${breakpoints.xsmall}) {
        padding-right: 0.4rem;
      }

      @media (min-width: ${breakpoints.medium}) {
        padding-right: 2.1rem;
        flex-basis: calc(25% - 2.4rem);
      }

      @media (min-width: ${breakpoints.xlarge}) {
        padding-right: 0;
        flex-basis: calc(20% - 1.4rem);
      }
    `};

  ${props => props.cardType === 'company' && props.twoColumnAtBreakpoint === 'large' && css`
      @media (min-width: ${breakpoints.xsmall}) {
        padding-right: 0.4rem;
      }

      @media (min-width: ${breakpoints.medium}) {
        padding-right: 0rem;
        flex-basis: calc(25% - 2.4rem);
      }

      @media (min-width: ${breakpoints.xlarge}) {
        flex-basis: calc(20% - 1.4rem);
      }
    `};

  ${props => props.threeColumnAtBreakpoint === 'xxlarge' && css`
      @media (min-width: ${breakpoints.xxlarge}) {
        flex-basis: calc(20% - 1.4rem);
      }
    `};
`;

const StyledHeader = styled.h3`
  margin: 0;
  width: 100%;

  a,
  span {
    font-family: ${fontFamily.secondary};
    font-size: 1.4rem;
    font-weight: ${fontWeight.bold};
    padding-right: 0.4rem;
    color: ${divisionColors.darker};

    &:hover {
      color: ${divisionColors.dark};
    }
  }

  span {
    color: ${colors.primaryFont};
  }
`;
const StyledCompanyCardWrapper = styled.div`
  font-family: ${fontFamily.primary};
  font-size: 1.2rem;
  line-height: 1.8rem;

  padding-top: 0.4rem;
  padding-right: 1.2rem;
  padding-bottom: 0.4rem;
  padding-left: 1.2rem;

  cursor: pointer;
  transition: border-left-color 0.2s;

  color: ${warmGrey.VIII};
  border: 0;
  background-color: ${warmGrey.I};

  ${props => props.selected && css`
      background-color: ${warmGrey.II};
    `}

  -webkit-font-smoothing: antialiased;

  @media (min-width: ${breakpoints.xsmall}) {
    position: relative;
  }

  ${props => props.twoColumnAtBreakpoint && ['medium', 'large'].includes(props.twoColumnAtBreakpoint) && css`
      @media (min-width: ${breakpoints[props.twoColumnAtBreakpoint]}) {
        display: flex;
        flex-direction: row;
      }
    `}

  &:hover {
    background-color: ${warmGrey.II};
  }

  .acuris-universal-card__meta {
    padding-right: 0;
  }
`;
const StyledDescription = styled.p`
  font-family: ${fontFamily.primary};
  font-size: 1.2rem;
  margin: 0.2rem 0;
`;
const StyledLocalCompanyName = styled.h4`
  font-family: ${fontFamily.primary};
  font-size: 1.2rem;
  margin: 0.2rem 0;
  color: #3f3d3d;
`;
const LabelList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
`;
const LabelListItem = styled.li`
  display: inline-block;
  margin: 0;
  padding: 0;

  @media (min-width: ${breakpoints.large}) {
    ${props => props.twoColumnAtBreakpoint === 'medium' && css`
        display: block;
      `}
  }
`;

const SituationLabelWrapper = styled(SituationLabel)`
  margin-right: 0.8rem;
`;

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }
const CompanyName = ({
  text,
  url,
  onClick,
  inactive
}) => {
  if (inactive) {
    return /*#__PURE__*/React.createElement(StyledHeader, null, /*#__PURE__*/React.createElement("span", null, text));
  }

  return /*#__PURE__*/React.createElement(StyledHeader, null, /*#__PURE__*/React.createElement("a", {
    onClick: onClick,
    href: url
  }, text));
};
const CompanyDescription = ({
  children
}) => /*#__PURE__*/React.createElement(StyledDescription, null, children);
const LocalCompanyName = ({
  children
}) => /*#__PURE__*/React.createElement(StyledLocalCompanyName, null, children);
const CompanyCardWrapper = ({
  className,
  children,
  companyID,
  inactive,
  onClickCard = _ => _,
  companyUrl,
  selected = false,
  twoColumnAtBreakpoint,
  threeColumnAtBreakpoint
}) => /*#__PURE__*/React.createElement(StyledCompanyCardWrapper, {
  className: className,
  "data-company-card-id": companyID,
  selected: selected,
  twoColumnAtBreakpoint: twoColumnAtBreakpoint,
  threeColumnAtBreakpoint: threeColumnAtBreakpoint,
  onClick: inactive ? undefined : () => {
    onClickCard(companyID);
    publish(events.companyCardClicked, {
      companyID
    });
    window.location.href = companyUrl;
  }
}, children);

const AcurisCompanyCard = ({
  className,
  companyName,
  companyUrl,
  companyID,
  companyDescription,
  inactive,
  localCompanyName,
  companyContentLinks,
  companyMetaData = {},
  situationLabels = [],
  customLabels = [],
  twoColumnAtBreakpoint,
  threeColumnAtBreakpoint,
  miniCard = false,
  onClickCard,
  hasFollowButton = false,
  isFollowing = false,
  selected = false,
  onFollowChange = _ => _,
  onSituationLabelClick
}) => {
  // TODO: make a constants files from this type of variables ???
  const cardType = 'company';
  return /*#__PURE__*/React.createElement(CompanyCardWrapper, {
    className: className,
    onClickCard: onClickCard,
    companyID: companyID,
    companyUrl: companyUrl,
    inactive: inactive,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint,
    miniCard: miniCard,
    selected: selected
  }, /*#__PURE__*/React.createElement(StyledCardMeta, {
    cardType: cardType,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint
  }, /*#__PURE__*/React.createElement(LabelList, null, situationLabels.length > 0 && situationLabels.map(situationLabel => {
    const onSituationLabelInternalClick = (e, situationLabelId) => {
      e.stopPropagation();
      typeof onSituationLabelClick === 'function' && onSituationLabelClick(situationLabelId);
      publish(events.companyCardSituationClicked, {
        companyID,
        labelText: situationLabel.stressedState
      });
    };

    return /*#__PURE__*/React.createElement(LabelListItem, {
      key: situationLabel.stressedState,
      twoColumnAtBreakpoint: twoColumnAtBreakpoint,
      threeColumnAtBreakpoint: threeColumnAtBreakpoint
    }, /*#__PURE__*/React.createElement(SituationLabelWrapper, _extends$2({}, situationLabel, {
      onClick: onSituationLabelInternalClick,
      isInactive: inactive
    })));
  }), customLabels.length > 0 && customLabels.map((customLabel, index) => {
    const onCustomLabelClick = e => {
      e.stopPropagation();
      publish(events.companyCardCustomLabelClicked, {
        companyID,
        labelText: customLabel.text
      });
      customLabel.onClick && customLabel.onClick(e);
    };

    return /*#__PURE__*/React.createElement(LabelListItem, {
      key: index,
      twoColumnAtBreakpoint: twoColumnAtBreakpoint,
      threeColumnAtBreakpoint: threeColumnAtBreakpoint
    }, /*#__PURE__*/React.createElement(CustomLabel, _extends$2({}, customLabel, {
      onClick: onCustomLabelClick,
      isInactive: inactive
    })));
  }))), /*#__PURE__*/React.createElement(StyledCardContentWrapper, {
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint,
    cardType: cardType,
    hasSituationLabel: situationLabels.length > 0
  }, /*#__PURE__*/React.createElement(StyledCardContent, null, /*#__PURE__*/React.createElement(CompanyName, {
    inactive: inactive,
    text: companyName,
    url: companyUrl,
    onClick: event => {
      event.stopPropagation();
      publish(events.companyCardNameClicked, {
        companyID
      });
    }
  }), localCompanyName && /*#__PURE__*/React.createElement(LocalCompanyName, null, localCompanyName), companyDescription && /*#__PURE__*/React.createElement(CompanyDescription, null, companyDescription), /*#__PURE__*/React.createElement(CompanyContentLinks, {
    companyID: companyID,
    companyContentLinks: companyContentLinks,
    inactive: inactive
  })), /*#__PURE__*/React.createElement(Tags, _extends$2({}, companyMetaData, {
    cardType: cardType,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint
  })), hasFollowButton && /*#__PURE__*/React.createElement(FollowButton, {
    onFollowChange: onFollowChange,
    companyName: companyName,
    isFollowing: isFollowing
  }) || ''));
};

const StyledDateTime = styled.span`
  white-space: nowrap;
  padding-right: 0.8rem;
  float: left;
  margin-top: 0.4rem;
`;

const DateTime = ({
  publishTime,
  userTimeZone = 'UTC',
  showDate = true,
  showTime = true
}) => {
  if (!showDate && !showTime) return null;
  const publishTimeWithTimezone = moment(publishTime).tz(userTimeZone);
  return /*#__PURE__*/React.createElement("time", {
    dateTime: publishTime
  }, showTime ? /*#__PURE__*/React.createElement(StyledDateTime, {
    "data-test-universal-card-time": true
  }, `${publishTimeWithTimezone.format('HH:mm z')}`) : null, showDate ? /*#__PURE__*/React.createElement(StyledDateTime, {
    "data-test-universal-card-date": true
  }, publishTimeWithTimezone.format('DD MMM YYYY')) : null);
};

const StyledCardHeadline = styled.a`
  font-size: 1.4rem;
  font-family: ${fontFamily.secondary};
  font-weight: ${fontWeight.bold};
  padding-right: 0.4rem;
  color: ${divisionColors.darker};

  &:hover {
    color: ${divisionColors.dark};
  }

  &:visited {
    color: ${warmGrey.VIII};
  }
`;

const Headline = ({
  handleHeadlineLinkClick,
  intelID,
  headlineUrl,
  headline
}) => {
  return /*#__PURE__*/React.createElement("h3", null, /*#__PURE__*/React.createElement(StyledCardHeadline, {
    onClick: handleHeadlineLinkClick,
    "data-test-intel-headline": `${intelID}`,
    href: headlineUrl
  }, headline));
};

const StyledProductName = styled.span`
  color: ${colors.primaryBrand};
  float: left;
  padding-right: 0.8rem;
  margin-top: 0.4rem;
  font-weight: 700;
`;

const ProductName = ({
  children
}) => {
  return /*#__PURE__*/React.createElement(StyledProductName, null, children);
};

const StyledTypeIndicator = styled.a`
  font-size: 1rem;
  font-weight: bold;

  display: inline-block;

  box-sizing: border-box;
  height: 1.8rem;
  margin-top: 0.4rem;
  margin-right: 0.8rem;
  padding: 0 0.4rem;

  color: ${colors.primaryFontInverse};
  background-color: ${divisionColors.darker};
  border-radius: 0.2rem;

  ${props => props.twoColumnAtBreakpoint && ['medium', 'large'].includes(props.twoColumnAtBreakpoint) && css`
      @media (min-width: ${breakpoints[props.twoColumnAtBreakpoint]}) {
        overflow: hidden;
        max-width: calc(100% - 10%);
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    `};

  &:visited {
    color: ${colors.primaryFontInverse};
  }

  &:hover,
  &:focus {
    background-color: ${divisionColors.darker};
    text-decoration: none;
  }
`;

const TypeIndicator = ({
  typeIndicatorData,
  twoColumnAtBreakpoint,
  threeColumnAtBreakpoint
}) => {
  if (!typeIndicatorData) {
    return null;
  }

  const {
    link,
    name,
    onClickHandler
  } = typeIndicatorData;
  return /*#__PURE__*/React.createElement(StyledTypeIndicator, {
    key: link,
    href: link,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint,
    onClick: onClickHandler,
    "data-test": "acuris-universal-card__type-indicator"
  }, name);
};

const StyledCardMetaContent = styled.div`
  margin-left: ${props => props.showCheckbox ? `2rem` : `0`};
  transition: margin-left 0.2s ease-in-out 0.2s;

  ${props => props.selectableStatus && css`
      margin-left: rem(20px);
      transition: margin-left ease-in-out 0.2s;
    `};

  ${props => props.twoColumnAtBreakpoint && ['medium', 'large'].includes(props.twoColumnAtBreakpoint) && css`
      @media (min-width: ${breakpoints[props.twoColumnAtBreakpoint]}) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
    `};

  ${props => props.threeColumnAtBreakpoint === 'xxlarge' && css`
      @media (min-width: ${breakpoints.large}) {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      @media (min-width: ${breakpoints.xxlarge}) {
        display: block;
        padding-right: 2.6rem;
        transition: padding-right 0.2s ease-in-out 0.2s;
      }
    `};
`;

const StyledNotificationCard = styled.div`
  font-family: ${fontFamily.primary};
  font-size: 1.2rem;
  line-height: 1.8rem;

  padding-right: 1.2rem;
  padding-left: 0.8rem;

  transition: border-left-color 0.2s;

  color: ${warmGrey.VIII};
  border-top: 0.1rem solid transparent;
  border-left: 0.4rem solid ${warmGrey.II};

  -webkit-font-smoothing: antialiased;

  @media (min-width: ${breakpoints.large}) {
    border-top: 0.1rem solid ${warmGrey.II};
  }

  .acuris-universal-card__proprietary-icon {
    position: absolute;
    top: 0.7rem;
    right: 0.4rem;

    @media (min-width: ${breakpoints.xsmall}) {
      right: auto;
    }
  }
`;

const NotificationCardWrapper = ({
  children,
  intelID
}) => {
  return /*#__PURE__*/React.createElement(StyledNotificationCard, {
    "data-test-intel-id": `${intelID}`
  }, children);
};

const AcurisNotificationCard = ({
  intelID,
  headline,
  headlineUrl,
  publishTime,
  userTimeZone,
  twoColumnAtBreakpoint,
  threeColumnAtBreakpoint,
  primaryTag,
  secondaryTags,
  isProprietary,
  handleHeadlineLinkClick,
  typeIndicator,
  productName
}) => {
  return /*#__PURE__*/React.createElement(NotificationCardWrapper, {
    intelID: intelID
  }, /*#__PURE__*/React.createElement(StyledCardMeta, {
    cardType: 'notification'
  }, /*#__PURE__*/React.createElement(StyledCardMetaContent, null, /*#__PURE__*/React.createElement(ProductName, null, productName), /*#__PURE__*/React.createElement(DateTime, {
    publishTime: publishTime,
    userTimeZone: userTimeZone
  }), /*#__PURE__*/React.createElement(TypeIndicator, {
    typeIndicatorData: typeIndicator,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint
  })), isProprietary && /*#__PURE__*/React.createElement(AcurisProprietaryIcon, {
    iconContainerClassName: "acuris-universal-card__proprietary-icon-container",
    svgClassName: "acuris-universal-card__proprietary-icon",
    pathClassName: "acuris-universal-card__proprietary-icon-path"
  })), /*#__PURE__*/React.createElement(StyledCardContentWrapper, {
    cardType: 'notification'
  }, /*#__PURE__*/React.createElement(StyledCardContent, null, /*#__PURE__*/React.createElement(Headline, {
    handleHeadlineLinkClick: handleHeadlineLinkClick,
    intelID: intelID,
    headlineUrl: headlineUrl,
    headline: headline
  })), /*#__PURE__*/React.createElement(Tags, {
    cardType: 'notification',
    primaryTag: primaryTag,
    secondaryTags: secondaryTags
  })));
};

const StyledDocumentLink = styled.a`
  margin-right: 0.8rem;
  color: ${divisionColors.darker};
  cursor: pointer;

  &:visited {
    color: ${divisionColors.darker};
  }
`;

function bytesToHuman(bytes) {
  if (!bytes) return null;
  if (bytes < 1000) return `${bytes} b`;

  if (bytes / 1e3 < 1000) {
    return `${(bytes / 1e3).toFixed(0)} kb`;
  }

  return `${(bytes / 1e6).toFixed(2)} Mb`;
}

function fileDetails(type, humanReadableSize) {
  return !humanReadableSize ? `${type.toUpperCase()}` : `${type.toUpperCase()} ${humanReadableSize}`;
}

const DocumentLink = ({
  filename,
  link,
  type,
  size,
  documentId,
  linkClickHandler
}) => /*#__PURE__*/React.createElement(StyledDocumentLink, {
  href: link,
  onClick: linkClickHandler,
  "data-document-link-id": documentId,
  target: "_blank"
}, `${filename} (${fileDetails(type, bytesToHuman(size))})`);

const StyledDocumentListWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const StyledDocumentList = styled.div`
  margin-top: 0.5rem;

  ul {
    line-height: 1.25;
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  li {
    word-break: break-word;
    display: block;

    @media (min-width: ${breakpoints.small}) {
      display: inline-block;
    }

    &:nth-child(2) {
      display: ${({
  expanded
}) => expanded ? `block` : `none`};

      @media (min-width: ${breakpoints.small}) {
        display: ${({
  expanded
}) => expanded ? `inline-block` : `none`};
      }
    }
  }
`;
const DocumentListExpandLink = styled.a`
  cursor: pointer;
  color: ${divisionColors.darker};
`;

// TODO: BUG document links appear to only show one by default
const DocumentList = ({
  documentList
}) => {
  const [isBlockExpanded, setBlockExpanded] = React.useState(false);

  const toggleExpanded = event => {
    event.stopPropagation();
    setBlockExpanded(!isBlockExpanded);
  };

  const showExpandButton = !isBlockExpanded && documentList.length >= 2;
  const documentsToRender = isBlockExpanded ? documentList : documentList.slice(0, 2);
  return /*#__PURE__*/React.createElement(StyledDocumentListWrapper, null, /*#__PURE__*/React.createElement(StyledDocumentList, {
    expanded: isBlockExpanded
  }, /*#__PURE__*/React.createElement("ul", null, documentsToRender.map(document => /*#__PURE__*/React.createElement("li", {
    key: document.documentId
  }, /*#__PURE__*/React.createElement(DocumentLink, document))))), showExpandButton && /*#__PURE__*/React.createElement(DocumentListExpandLink, {
    onClick: toggleExpanded
  }, "+ Show more"));
};

const StyledCheckbox = styled(Checkbox)`
  position: absolute;
  top: 0.3rem;
  left: 1.3rem;

  width: 1.4rem;
  height: 1.8rem;
  margin: 0;
  padding: 0;

  transition: ${props => props.showCheckbox ? css`transform .2s linear .2s` : css`transform linear 200ms`};
  transform: ${props => props.showCheckbox ? css`scale(1)` : css`scale(0)`};
  transform-origin: 50% 50%;

  flex-shrink: 0;
`;

// import ProprietaryIcon from "?"
// .acuris-universal-card__tag-link,
// a.acuris-universal-card__tag-link:visited {
// .acuris-checkbox {const
const StyledCard = styled.div`
  font-family: ${fontFamily.primary};
  font-size: 1.2rem;
  line-height: 1.8rem;

  padding-right: 1.2rem;
  padding-left: 0.8rem;

  transition: border-left-color 0.2s;

  color: ${warmGrey.VIII};
  border-top: 0.1rem solid transparent;
  border-left: 0.4rem solid ${warmGrey.II};

  -webkit-font-smoothing: antialiased;

  @media (min-width: ${breakpoints.large}) {
    border-top: 0.1rem solid ${warmGrey.II};
  }

  ${props => props.showCheckbox && css`
      padding-top: 0.1rem;
      border-top: 0;
      border-left-color: ${warmGrey.VI};
    `};

  ${props => props.showCheckbox && props.clickableCard && css`
    ${StyledTagItem},
    ${TagSource},
    ${StyledTagItem} a,
    ${StyledCardHeadline},
    ${StyledDocumentList},
    ${DocumentListExpandLink},
    ${StyledDocumentLink},
    ${StyledTypeIndicator} {
        pointer-events: none;
        cursor: default;
        display: inline-block;
      }
    }

    border-left-color: ${colors.primaryFont};

    &:hover {
      color: $primary-font-color--inverse;
      background-color: ${warmGrey.IX};
      border-left-color: ${warmGrey.VI};
      cursor: pointer;

      ${styleCheckbox({
  showCheck: true,
  checkColor: warmGrey.VII,
  backgroundColor: colors.primaryBackground,
  checkboxBorderColor: colors.primaryFont
})}
    }

    &&&:hover {
      ${StyledDateTime},
      ${StyledTagItem},
      ${TagSource},
      ${StyledTagItem} a,
      ${StyledCardHeadline},
      ${StyledDocumentList},
      ${DocumentListExpandLink},
      ${StyledDocumentLink},
      ${StyledTypeIndicator} {
          color: ${colors.primaryFontInverse};
      }

      .acuris-universal-card__proprietary-icon-path {
        fill: ${colors.primaryFontInverse};
      }

      ${StyledTypeIndicator} {
        background-color: ${warmGrey.VIII};
      }

    }
  `}

  ${props => props.showCheckbox && props.selected && css`
      a.acuris-universal-card__tag-link:visited {
        color: $primary-font-color--inverse;
      }

      .acuris-universal-card__type-indicator {
        background-color: map-get($warm-grey, 8);
      }

      .acuris-universal-card__proprietary-icon-path {
        fill: $primary-font-color--inverse;
      }

      ${StyledCheckbox} {
        input:not(:disabled):not(:checked):not(:indeterminate) ~ .checkbox__text:after,
        input:not(:disabled):not(:checked):not(:indeterminate) ~ .acuris-checkbox__text:after {
          border-color: map-get($warm-grey, 7);
          border-style: solid;
          border-width: 0 $standard-border-width $standard-border-width 0;
          height: 8px;
          width: 4px;
          top: 1px;
          left: 4px;
          transform: rotate(45deg);
        }

        input:not(:disabled):not(:checked):not(:indeterminate) ~ .checkbox__text:after {
          border-color: transparent;
        }
      }
    }


  `};

  ${props => props.clickableCard && `cursor: default;`};

  ${props => props.twoColumnAtBreakpoint && ['medium', 'large'].includes(props.twoColumnAtBreakpoint) && css`
      @media (min-width: ${breakpoints[props.twoColumnAtBreakpoint]}) {
        display: flex;
        flex-direction: row;
      }
    `}

  .acuris-universal-card__proprietary-icon {
    ${props => props.twoColumnAtBreakpoint && ['medium', 'large'].includes(props.twoColumnAtBreakpoint) && css`
        @media (min-width: ${breakpoints[props.twoColumnAtBreakpoint]}) {
          position: absolute;
          top: 0.7rem;
          right: 0.4rem;
        }
      `};
  }
`;

const AcurisUniversalCard = ({
  intelID,
  headline,
  headlineUrl,
  publishTime,
  userTimeZone,
  twoColumnAtBreakpoint,
  threeColumnAtBreakpoint,
  pillTag,
  primaryTag,
  secondaryTags,
  isProprietary,
  handleHeadlineLinkClick,
  handleCheckboxChange,
  typeIndicator,
  showCheckbox,
  selected = false,
  selectableStatus,
  showDate,
  showTime,
  documentList,
  sources
}) => {
  const clickableCard = showCheckbox && selectableStatus;

  const onCheckboxChange = e => {
    e.preventDefault();
    handleCheckboxChange(!selected);
  };

  return /*#__PURE__*/React.createElement(StyledCard, {
    showCheckbox: showCheckbox,
    selected: selected,
    clickableCard: clickableCard,
    onClick: clickableCard ? onCheckboxChange : undefined,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint,
    "data-test-intel-id": true
  }, /*#__PURE__*/React.createElement(StyledCardMeta, {
    showCheckbox: showCheckbox,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint
  }, /*#__PURE__*/React.createElement(StyledCheckbox, {
    showCheckbox: showCheckbox,
    className: "acuris-checkbox",
    size: 'small',
    label: headline,
    checked: selected,
    onChange: onCheckboxChange,
    autoComplete: "off",
    id: intelID,
    disabled: !showCheckbox
  }), /*#__PURE__*/React.createElement(StyledCardMetaContent, {
    showCheckbox: showCheckbox,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint
  }, /*#__PURE__*/React.createElement(DateTime, {
    publishTime: publishTime,
    userTimeZone: userTimeZone,
    showDate: showDate,
    showTime: showTime
  }), /*#__PURE__*/React.createElement(TypeIndicator, {
    typeIndicatorData: typeIndicator,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint
  })), isProprietary && /*#__PURE__*/React.createElement(AcurisProprietaryIcon, {
    iconContainerClassName: "acuris-universal-card__proprietary-icon-container",
    svgClassName: "acuris-universal-card__proprietary-icon",
    pathClassName: "acuris-universal-card__proprietary-icon-path"
  })), /*#__PURE__*/React.createElement(StyledCardContentWrapper, {
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint,
    selectableStatus: selectableStatus,
    showCheckbox: showCheckbox
  }, /*#__PURE__*/React.createElement(StyledCardContent, null, /*#__PURE__*/React.createElement(Headline, {
    handleHeadlineLinkClick: handleHeadlineLinkClick,
    intelID: intelID,
    headlineUrl: headlineUrl,
    headline: headline
  }), Array.isArray(documentList) && documentList.length > 0 && /*#__PURE__*/React.createElement(DocumentList, {
    documentList: documentList
  })), /*#__PURE__*/React.createElement(Tags, {
    pillTag: pillTag,
    primaryTag: primaryTag,
    secondaryTags: secondaryTags,
    sources: sources,
    twoColumnAtBreakpoint: twoColumnAtBreakpoint,
    threeColumnAtBreakpoint: threeColumnAtBreakpoint
  })));
};

const AcurisUniversalCardListItem = styled.li`
  margin-bottom: 0.8rem;
`;

export { AcurisCompanyCard, AcurisNotificationCard, AcurisUniversalCard, AcurisUniversalCardListItem, FollowButton };
//# sourceMappingURL=index.js.map
